/**
 * Estimate input/output tokens and map platform → provider
 * for linking prompts to the LLM Cost Calculator.
 */

// ── Rough input-token count (≈ 1 token per 4 chars) ──────────────────────────
export function estimateInputTokens(text) {
  if (!text) return 0;
  return Math.ceil(text.length / 4);
}

// ── Task-type heuristics for output tokens ────────────────────────────────────
const TASK_PATTERNS = [
  { regex: /\b(summar|tldr|recap|condense|shorten)\b/i,             type: 'summarization' },
  { regex: /\b(translat|convert .+ to .+ language)\b/i,             type: 'translation' },
  { regex: /\b(classif|categori|label|tag|extract|parse|detect)\b/i,type: 'classification' },
  { regex: /\b(code|function|script|program|implement|debug|refactor|regex|sql|api)\b/i, type: 'code' },
  { regex: /\b(email|subject line|cold outreach|follow.?up)\b/i,    type: 'email' },
  { regex: /\b(essay|article|blog|story|write .{0,20}(page|word|paragraph))\b/i, type: 'creative' },
  { regex: /\b(explain|what is|how does|why|define|describe)\b/i,   type: 'qa' },
];

const CATEGORY_TASK_MAP = {
  'Programming':          'code',
  'Content':              'creative',
  'Copywriting':          'creative',
  'Marketing':            'creative',
  'Social Media':         'creative',
  'SEO':                  'creative',
  'Email':                'email',
  'Sales':                'email',
  'Business':             'qa',
  'Learning':             'qa',
  'Resume':               'structured',
  'Interview':            'structured',
  'Project Management':   'structured',
  'Personal Development': 'qa',
  'Visual Content':       'image_prompt',
  'Fitness':              'qa',
};

const OUTPUT_ESTIMATES = {
  summarization:  (inputTok) => Math.max(50, Math.ceil(inputTok * 0.25)),
  translation:    (inputTok) => Math.ceil(inputTok * 1.1),
  classification: () => 30,
  code:           () => 500,
  email:          () => 270,
  creative:       () => 1000,
  qa:             () => 150,
  structured:     () => 400,
  image_prompt:   () => 150,
  chat:           () => 150,
  default:        () => 200,
};

export function estimateOutputTokens(text, categories = []) {
  const inputTok = estimateInputTokens(text);

  // 1. Check content for task-type keywords
  for (const { regex, type } of TASK_PATTERNS) {
    if (regex.test(text)) {
      return OUTPUT_ESTIMATES[type](inputTok);
    }
  }

  // 2. Fall back to category mapping
  for (const cat of categories) {
    const type = CATEGORY_TASK_MAP[cat];
    if (type) return OUTPUT_ESTIMATES[type](inputTok);
  }

  return OUTPUT_ESTIMATES.default(inputTok);
}

// ── Platform name → LLM provider ─────────────────────────────────────────────
const PLATFORM_PROVIDER = {
  'ChatGPT':    'OpenAI',
  'DALL-E':     'OpenAI',
  'Claude':     'Anthropic',
  'Gemini':     'Google',
};

export function platformToProvider(platformName) {
  return PLATFORM_PROVIDER[platformName] || '';
}

// ── Build calculator URL with query params ───────────────────────────────────
export function buildCalcUrl(prompt, platformName) {
  const inputTokens  = estimateInputTokens(prompt.content);
  const outputTokens = estimateOutputTokens(prompt.content, prompt.categories);

  const params = new URLSearchParams();
  params.set('input', inputTokens);
  params.set('output', outputTokens);
  params.set('calls', '1');
  if (prompt.title) params.set('title', prompt.title.slice(0, 80));

  return `/LLM-Cost-Calculator?${params.toString()}`;
}
